import type { ModelRoute, VelugaModelCatalogItem } from './service.js';

export type RealtimeVoiceProtocol = 'openai-live' | 'gemini-live';
import type { SpeechProtocol, SpeechToTextProtocol, TextToSpeechProtocol } from './desktop-model.js';
export type { ModelInteractionKind, SpeechProtocol, SpeechToTextProtocol, TextToSpeechProtocol } from './desktop-model.js';
import type { RealtimeVoicePolicy, SpeechToTextPolicy, TextToSpeechPolicy } from './policy.js';
export type { RealtimeVoicePolicy, SpeechToTextPolicy, TextToSpeechPolicy } from './policy.js';

type SpeechTokenField = 'audioInputTokens' | 'audioOutputTokens' | 'textInputTokens' | 'textOutputTokens';
/**
 * Protocol → kind, provider and billing unit. `requiredTokens` must be present in terminal
 * evidence. `pricedTokens` (a superset) is every axis the provider reports on a normal
 * request; a rate must price all of them, or every request stays settlement-pending.
 */
export const SPEECH_PROTOCOLS: Record<SpeechProtocol, { kind: 'speech_to_text' | 'text_to_speech'; provider: 'openai' | 'google';
  unit: SpeechUsage['unit']; requiredTokens: readonly SpeechTokenField[]; pricedTokens: readonly SpeechTokenField[] }> = {
  // Billed by recognized input audio length, not tokens.
  'openai-transcription': { kind: 'speech_to_text', provider: 'openai', unit: 'audio_duration', requiredTokens: [], pricedTokens: [] },
  // Observed 2026-10-03: one text input token (instructions) on every request.
  'gemini-transcription': { kind: 'speech_to_text', provider: 'google', unit: 'token_breakdown', requiredTokens: ['audioInputTokens', 'textOutputTokens'],
    pricedTokens: ['audioInputTokens', 'textOutputTokens', 'textInputTokens'] },
  // Realtime also bills the transcript it emits alongside the audio.
  'openai-realtime-tts': { kind: 'text_to_speech', provider: 'openai', unit: 'token_breakdown', requiredTokens: ['textInputTokens', 'textOutputTokens', 'audioOutputTokens'],
    pricedTokens: ['textInputTokens', 'textOutputTokens', 'audioOutputTokens'] },
  // Observed 2026-10-03: a fixed voice-prompt audio input on every request.
  'gemini-tts': { kind: 'text_to_speech', provider: 'google', unit: 'token_breakdown', requiredTokens: ['textInputTokens', 'audioOutputTokens'],
    pricedTokens: ['textInputTokens', 'audioOutputTokens', 'audioInputTokens'] },
};
const VOICE_ROUTE_PROTOCOLS: readonly string[] = ['openai-live', 'gemini-live', ...Object.keys(SPEECH_PROTOCOLS)];

// Product ceilings (contract D05), smaller than any provider limit.
export const SPEECH_STT_MAX_INPUT_DURATION_MS = 120 * 1000;
export const SPEECH_TTS_MAX_INPUT_CODE_POINTS = 2000;
/** One-shot request bounds: STT WAV upload size and the whole-request deadline. */
export const SPEECH_STT_MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
export const SPEECH_REQUEST_DEADLINE_MS = 120 * 1000;

/**
 * Gateway speech responses send `X-Request-Id` before the provider call, so the
 * HTTP status is not the outcome. The completed JSON body is: exactly one
 * terminal; a partial body, EOF or timeout is a failure.
 */
export type SpeechTerminalErrorCode = 'cancelled' | 'access_revoked' | 'timeout' | 'provider_rejected' |
  'provider_rate_limited' | 'provider_error' | 'invalid_provider_response';
export type SpeechTerminal<T extends object> =
  | ({ ok: true; requestId: string } & T)
  | { ok: false; requestId: string; error: SpeechTerminalErrorCode };
export type SpeechTranscriptionTerminal = SpeechTerminal<{ text: string }>;
/** Normalized PCM16LE mono 24 kHz WAV, complete before playback. */
export type SpeechSynthesisTerminal = SpeechTerminal<{ mimeType: 'audio/wav'; audio: string; byteLength: number }>;
/** TTS bounds (D05): UTF-8 input and decoded WAV output. */
export const SPEECH_TTS_MAX_INPUT_BYTES = 8 * 1024;
export const SPEECH_TTS_MAX_OUTPUT_BYTES = 8 * 1024 * 1024;
export const SPEECH_TTS_MAX_OUTPUT_DURATION_MS = 120 * 1000;

const SPEECH_TERMINAL_ERRORS: readonly string[] = ['cancelled', 'access_revoked', 'timeout', 'provider_rejected',
  'provider_rate_limited', 'provider_error', 'invalid_provider_response'];
/** Rejects anything but a complete terminal for the expected request. */
export function isSpeechTranscriptionTerminal(value: unknown, requestId: string): value is SpeechTranscriptionTerminal {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (v.requestId !== requestId) return false;
  if (v.ok === true) return Object.keys(v).length === 3 && typeof v.text === 'string';
  return v.ok === false && Object.keys(v).length === 3 && SPEECH_TERMINAL_ERRORS.includes(v.error as string);
}

/** Success also requires the declared length to match the decoded audio. */
export function isSpeechSynthesisTerminal(value: unknown, requestId: string): value is SpeechSynthesisTerminal {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (v.requestId !== requestId) return false;
  if (v.ok === false) return Object.keys(v).length === 3 && SPEECH_TERMINAL_ERRORS.includes(v.error as string);
  return v.ok === true && Object.keys(v).length === 5 && v.mimeType === 'audio/wav' && typeof v.audio === 'string' &&
    Number.isSafeInteger(v.byteLength) && (v.byteLength as number) > 44 && (v.byteLength as number) <= SPEECH_TTS_MAX_OUTPUT_BYTES &&
    /^[A-Za-z0-9+/]*={0,2}$/.test(v.audio) && Math.floor(v.audio.length * 3 / 4) - (v.audio.match(/=*$/)?.[0].length ?? 0) === v.byteLength;
}

/** Server catalog metadata for one-shot speech; never a chat or live-session model. */
export type SpeechModelMetadata =
  | { protocol: SpeechToTextProtocol; input: VoicePcmFormat; maxInputDurationMs: number }
  | { protocol: TextToSpeechProtocol; output: VoicePcmFormat; voices: string[]; maxInputCodePoints: number };

/** Tenant STT/TTS policy. The realtime child stays in `Tenant.realtimeVoicePolicy`. */
export interface TenantSpeechPolicy {
  stt: SpeechToTextPolicy;
  tts: TextToSpeechPolicy;
}
/** Tenant STT/TTS projection; `policyVersion` is independent of the global featureRevision. */
export interface VoicePolicy extends TenantSpeechPolicy {
  policyVersion: number;
}

export const REALTIME_VOICE_PROVIDER_READINESS = {
  'openai-live': {
    funding: {
      verified: false,
      intervalMs: 30000,
      closeAllowanceMs: 15000,
      maximumUsage: { unit: 'session_duration', seconds: 47 } as const,
    },
    // GPT-Live defaults to store:false and has documented retention behavior.
    // ZDR/MAM and invoice reconciliation remain false until the project is verified.
    dataControls: { verified: false, zdr: false, mam: false },
  },
  'gemini-live': {
    funding: {
      verified: false,
      intervalMs: 30000,
      closeAllowanceMs: 15000,
      // Beta reservation estimate, NOT a verified provider bound. The gateway admits
      // only complete response subtotals covered by the currently reserved credits.
      maximumUsage: { unit: 'token_breakdown', audioInputTokens: 16384, audioOutputTokens: 1024,
        textInputTokens: 16384, textOutputTokens: 1024 } as const,
    },
    dataControls: { verified: false, zdr: false, mam: false },
  },
} as const;

export function parseRealtimeVoiceTenantIds(value: string | undefined): ReadonlySet<string> {
  if (!value?.trim()) return new Set();
  const ids = value.split(',').map(id => id.trim());
  if (ids.length > 100 || ids.some(id => !/^[a-zA-Z0-9_-]{1,200}$/.test(id))) {
    throw new Error('invalid_realtime_voice_tenant_ids');
  }
  return new Set(ids);
}

export type RealtimeVoiceProvider = 'openai' | 'gemini';
export interface RealtimeVoiceAdminView {
  tenantId: string;
  policy?: RealtimeVoicePolicy;
  models: Array<{ id: string; displayName: string; billingUnit: RealtimeVoiceUsage['unit']; status: 'active' | 'deprecated' | 'disabled' }>;
}
export type RealtimeVoiceAdminWrite =
  | { action: 'register'; provider: RealtimeVoiceProvider }
  | { action: 'activate'; modelId: string }
  | { action: 'disable'; modelId: string }
  | { action: 'policy'; policy: RealtimeVoicePolicy };

export function isRealtimeVoicePolicy(value: unknown): value is RealtimeVoicePolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const p = value as Record<string, unknown>;
  return Object.keys(p).every(key => ['enabled', 'allowedModelIds', 'maxSessionDurationMs', 'maxConcurrentSessions', 'requirePreTransmissionInspection', 'requireDurableSharedBudget'].includes(key)) &&
    typeof p.enabled === 'boolean' && typeof p.requirePreTransmissionInspection === 'boolean' &&
    typeof p.requireDurableSharedBudget === 'boolean' &&
    typeof p.maxSessionDurationMs === 'number' && Number.isSafeInteger(p.maxSessionDurationMs) && p.maxSessionDurationMs > 0 && p.maxSessionDurationMs <= VOICE_SESSION_HARD_CAP_MS &&
    p.maxConcurrentSessions === 1 &&
    (p.allowedModelIds === undefined || (Array.isArray(p.allowedModelIds) && p.allowedModelIds.length <= 100 &&
      p.allowedModelIds.every(id => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,200}$/.test(id))));
}

const isModelIdList = (value: unknown) => value === undefined || (Array.isArray(value) && value.length <= 100 &&
  value.every(id => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,200}$/.test(id)));
const isSpeechKindPolicy = (value: unknown, limitKey: string, ceiling: number) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const p = value as Record<string, unknown>;
  const limit = p[limitKey];
  return Object.keys(p).every(key => ['enabled', 'allowedModelIds', limitKey].includes(key)) &&
    typeof p.enabled === 'boolean' && isModelIdList(p.allowedModelIds) &&
    typeof limit === 'number' && Number.isSafeInteger(limit) && limit > 0 && limit <= ceiling;
};

export function isTenantSpeechPolicy(value: unknown): value is TenantSpeechPolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const p = value as Record<string, unknown>;
  return Object.keys(p).every(key => key === 'stt' || key === 'tts') &&
    isSpeechKindPolicy(p.stt, 'maxInputDurationMs', SPEECH_STT_MAX_INPUT_DURATION_MS) &&
    isSpeechKindPolicy(p.tts, 'maxInputCodePoints', SPEECH_TTS_MAX_INPUT_CODE_POINTS);
}

/** Absent or malformed stored policy projects to every kind OFF. */
export function tenantVoicePolicy(tenant: { speechPolicy?: TenantSpeechPolicy; voicePolicyVersion?: number }): VoicePolicy {
  const speech = isTenantSpeechPolicy(tenant.speechPolicy) ? tenant.speechPolicy : undefined;
  return {
    policyVersion: tenant.voicePolicyVersion ?? 0,
    stt: speech?.stt ?? { enabled: false, maxInputDurationMs: SPEECH_STT_MAX_INPUT_DURATION_MS },
    tts: speech?.tts ?? { enabled: false, maxInputCodePoints: SPEECH_TTS_MAX_INPUT_CODE_POINTS },
  };
}

export type VoiceUnavailableReason = 'disabled' | 'entitlement_denied' | 'policy_denied' |
  'model_not_allowed' | 'data_controls_unverified' | 'route_not_ready' |
  'billing_not_ready' | 'funding_unavailable';

export interface DesktopVoiceModel {
  id: string;
  displayName: string;
  /** Additive disclosure metadata; older servers may omit it. */
  provider?: RealtimeVoiceProvider;
  voices: string[];
  input: VoicePcmFormat;
  output: VoicePcmFormat;
  available: boolean;
  reasonCodes: VoiceUnavailableReason[];
}

export interface RealtimeVoiceFeatureSettings {
  enabled: boolean;
  revision: number;
  updatedAt: string;
}
export interface RealtimeVoiceFeatureSettingsWrite { enabled: boolean }
/** The same platform singleton, now the master for every voice kind. */
export type VoiceFeatureSettings = RealtimeVoiceFeatureSettings;
export interface VoiceFeatureSettingsWrite { enabled: boolean; expectedFeatureRevision: number }

/** Speech feature names; active models grant them automatically to desktop users. */
export const SPEECH_ENTITLEMENT_FEATURES = { stt: 'speech-to-text', tts: 'text-to-speech' } as const;
export const VOICE_KIND_FEATURES = {
  realtime_voice: 'realtime-voice',
  speech_to_text: SPEECH_ENTITLEMENT_FEATURES.stt,
  text_to_speech: SPEECH_ENTITLEMENT_FEATURES.tts,
} as const;

/**
 * Per-protocol execution readiness. A flag turns true only with adapter,
 * provider-billing or data-control evidence; until then models stay disabled.
 */
export interface SpeechProviderReadiness {
  adapter: boolean;
  funding: boolean;
  dataControls: { verified: boolean; zdr: boolean; mam: boolean };
}
// Evidence: 2026-10-03 dev paid adapter calls (docs/archive/voice-tts-stt/README.md#archived-detail-10-pr-10-acceptance-md).
// No ZDR or MAM agreement is recorded, so tenants requiring either stay closed.
export const SPEECH_PROVIDER_READINESS: Record<SpeechProtocol, SpeechProviderReadiness> = {
  'openai-transcription': { adapter: true, funding: true, dataControls: { verified: true, zdr: false, mam: false } },
  'gemini-transcription': { adapter: true, funding: true, dataControls: { verified: true, zdr: false, mam: false } },
  'openai-realtime-tts': { adapter: true, funding: true, dataControls: { verified: true, zdr: false, mam: false } },
  'gemini-tts': { adapter: true, funding: true, dataControls: { verified: true, zdr: false, mam: false } },
};

/** Registration presets: exact upstream ids from the 2026-10-02 research, re-verified on registration day. */
export const SPEECH_PRESETS = {
  'openai-transcribe': { protocol: 'openai-transcription', upstreamModelRef: 'gpt-transcribe', displayName: 'GPT Transcribe' },
  'gemini-transcribe': { protocol: 'gemini-transcription', upstreamModelRef: 'gemini-3.5-transcribe', displayName: 'Gemini 3.5 Transcribe' },
  'openai-realtime-mini-tts': { protocol: 'openai-realtime-tts', upstreamModelRef: 'gpt-realtime-2.1-mini', displayName: 'GPT Realtime 2.1 Mini TTS', voices: ['marin'] },
  'gemini-flash-tts': { protocol: 'gemini-tts', upstreamModelRef: 'gemini-3.8-flash-tts', displayName: 'Gemini 3.8 Flash TTS', voices: ['Kore'] },
  'gemini-flash-lite-tts': { protocol: 'gemini-tts', upstreamModelRef: 'gemini-3.8-flash-lite-tts', displayName: 'Gemini 3.8 Flash Lite TTS', voices: ['Kore'] },
} as const satisfies Record<string, { protocol: SpeechProtocol; upstreamModelRef: string; displayName: string; voices?: readonly string[] }>;
export type SpeechPresetId = keyof typeof SPEECH_PRESETS;

/** Separate readiness axes; `featureEnabled` is never a synonym for any of them. */
export interface VoiceModelReadiness {
  /** Route and provider key present. */
  configured: boolean;
  adapterReady: boolean;
  /** A published rate prices every axis the protocol bills. */
  rateReady: boolean;
  dataReady: boolean;
  /** In the tenant model assignment. */
  allowed: boolean;
}
export interface VoiceAdminModel {
  id: string;
  displayName: string;
  kind: 'realtime_voice' | 'speech_to_text' | 'text_to_speech';
  protocol: RealtimeVoiceProtocol | SpeechProtocol;
  status: 'active' | 'deprecated' | 'disabled';
  readiness: VoiceModelReadiness;
}
export interface TenantVoiceAdminView {
  tenantId: string;
  policyVersion: number;
  realtime?: RealtimeVoicePolicy;
  stt: SpeechToTextPolicy;
  tts: TextToSpeechPolicy;
  models: VoiceAdminModel[];
}
/** Each present child replaces that kind only. */
export interface TenantVoicePolicyWrite {
  expectedPolicyVersion: number;
  realtime?: RealtimeVoicePolicy;
  stt?: SpeechToTextPolicy;
  tts?: TextToSpeechPolicy;
}
export type VoiceModelAdminWrite =
  | { action: 'register'; preset: SpeechPresetId }
  | { action: 'activate'; modelId: string }
  | { action: 'disable'; modelId: string };

export interface DesktopRealtimeVoiceAvailability {
  featureEnabled?: boolean;
  featureRevision?: number;
  enabled: boolean;
  models: DesktopVoiceModel[];
  defaultVoiceModelId?: string;
  maxSessionDurationMs: number;
  maxConcurrentSessions: number;
  reasonCodes: VoiceUnavailableReason[];
}

/** Old servers and malformed snapshots must not expose voice controls. */
export function isDesktopRealtimeVoiceAvailability(value: unknown): value is DesktopRealtimeVoiceAvailability {
  if (!value || typeof value !== 'object') return false;
  const v = value as DesktopRealtimeVoiceAvailability;
  return typeof v.featureEnabled === 'boolean' && Number.isSafeInteger(v.featureRevision) && v.featureRevision! >= 0 &&
    typeof v.enabled === 'boolean' && (!v.enabled || v.featureEnabled) &&
    Number.isFinite(v.maxSessionDurationMs) && Number.isFinite(v.maxConcurrentSessions) &&
    Array.isArray(v.reasonCodes) && v.reasonCodes.every(reason => typeof reason === 'string') &&
    Array.isArray(v.models) && v.models.every(model => model && typeof model.id === 'string' &&
      typeof model.displayName === 'string' && typeof model.available === 'boolean' &&
      Array.isArray(model.voices) && model.voices.every(voice => typeof voice === 'string') &&
      Array.isArray(model.reasonCodes) && model.input?.encoding === 'pcm_s16le' && model.output?.encoding === 'pcm_s16le' &&
      [16000, 24000].includes(model.input.sampleRate) && [16000, 24000].includes(model.output.sampleRate));
}

export interface DesktopSpeechModel {
  id: string;
  displayName: string;
  provider: RealtimeVoiceProvider;
  /** TTS only. */
  voices?: string[];
  available: boolean;
  reasonCodes: VoiceUnavailableReason[];
}
export interface DesktopSpeechKindAvailability {
  /** Display gate: master, kind deployment gate, managed account and admin kind allow. */
  featureEnabled: boolean;
  /** Execution hint; the gateway repeats every check. */
  enabled: boolean;
  models: DesktopSpeechModel[];
  defaultModelId?: string;
  reasonCodes: VoiceUnavailableReason[];
}
/** Per-kind projection. The legacy `DesktopRealtimeVoiceAvailability` keeps its own meaning. */
export interface DesktopVoiceAvailability {
  featureRevision: number;
  policyVersion: number;
  /** Unlike the legacy DTO, featureEnabled here also requires the admin realtime allow. */
  realtime: DesktopRealtimeVoiceAvailability;
  stt: DesktopSpeechKindAvailability & { maxInputDurationMs: number };
  tts: DesktopSpeechKindAvailability & { maxInputCodePoints: number };
}

const isSpeechKindAvailability = (value: unknown, limitKey: string) => {
  if (!value || typeof value !== 'object') return false;
  const v = value as DesktopSpeechKindAvailability & Record<string, unknown>;
  return typeof v.featureEnabled === 'boolean' && typeof v.enabled === 'boolean' && (!v.enabled || v.featureEnabled) &&
    Number.isSafeInteger(v[limitKey]) && (v[limitKey] as number) >= 0 &&
    Array.isArray(v.reasonCodes) && v.reasonCodes.every(reason => typeof reason === 'string') &&
    Array.isArray(v.models) && v.models.every(model => model && typeof model.id === 'string' &&
      typeof model.displayName === 'string' && typeof model.available === 'boolean' &&
      (model.provider === 'openai' || model.provider === 'gemini') && Array.isArray(model.reasonCodes) &&
      (model.voices === undefined || (Array.isArray(model.voices) && model.voices.every(voice => typeof voice === 'string')))) &&
    (v.defaultModelId === undefined || v.models.some(model => model.id === v.defaultModelId && model.available));
};

/** Missing fields or revisions fail closed; callers then hide every voice entry point. */
export function isDesktopVoiceAvailability(value: unknown): value is DesktopVoiceAvailability {
  if (!value || typeof value !== 'object') return false;
  const v = value as DesktopVoiceAvailability;
  return Number.isSafeInteger(v.featureRevision) && v.featureRevision >= 0 &&
    Number.isSafeInteger(v.policyVersion) && v.policyVersion >= 0 &&
    isDesktopRealtimeVoiceAvailability(v.realtime) && v.realtime.featureRevision === v.featureRevision &&
    isSpeechKindAvailability(v.stt, 'maxInputDurationMs') && isSpeechKindAvailability(v.tts, 'maxInputCodePoints');
}

export interface VoicePcmFormat {
  encoding: 'pcm_s16le';
  sampleRate: 16000 | 24000;
  channels: 1;
}

/** Server catalog metadata; provider model IDs and credentials stay in routing. */
export interface RealtimeVoiceMetadata {
  protocol: RealtimeVoiceProtocol;
  input: VoicePcmFormat;
  output: VoicePcmFormat;
  voices: string[];
  delegation: 'client' | 'function_call';
  maxSessionDurationMs: number;
}

export const VOICE_PROTOCOL_VERSION = 1 as const;
export const VOICE_CONTROL_MAX_BYTES = 64 * 1024;
export const VOICE_PACKET_MAX_MS = 100;
export const VOICE_QUEUE_MAX_MS = 2000;
// Desktop unplayed-output bound. Providers may deliver a whole reply faster than
// realtime (Gemini maxOutputTokens 256 is roughly 8-10 s of audio), so this must
// hold one full reply; the wire queues above stay at VOICE_QUEUE_MAX_MS.
export const VOICE_PLAYBACK_QUEUE_MAX_MS = 15000;
// Product ceilings, not evidence of a provider billing bound.
export const VOICE_SESSION_HARD_CAP_MS = 10 * 60 * 1000;
export const VOICE_IDLE_TIMEOUT_MS = 60 * 1000;
export const VOICE_CHECKPOINT_INTERVAL_MS = 30 * 1000;

export interface VoiceSessionIdentity {
  voiceSessionId: string;
  connectionEpoch: number;
}

export type RealtimeVoiceUsage =
  | { unit: 'session_duration'; seconds: number }
  | {
      unit: 'token_breakdown';
      audioInputTokens?: number;
      audioOutputTokens?: number;
      textInputTokens?: number;
      textOutputTokens?: number;
      cachedAudioInputTokens?: number;
      cachedTextInputTokens?: number;
    };

interface RealtimeVoiceRateBase {
  version: string;
  catalogModelId: string;
  effectiveFrom: string;
  marginMultiplier: number;
  sourceUrl: string;
  verifiedAt: string;
}
export type RealtimeVoiceRate = RealtimeVoiceRateBase & (
  | { unit: 'session_duration'; usdPerMinute: number }
  | { unit: 'token_breakdown'; audioInputUsdPerMillion: number; audioOutputUsdPerMillion: number;
      textInputUsdPerMillion: number; textOutputUsdPerMillion: number;
      cachedAudioInputUsdPerMillion?: number; cachedTextInputUsdPerMillion?: number }
);
export type RealtimeVoiceRateInput = RealtimeVoiceRate extends infer R
  ? R extends RealtimeVoiceRate ? Omit<R, 'version' | 'effectiveFrom'> : never : never;

/** Content-free observation stored once per connection, separately from task usage. */
export interface RealtimeVoiceUsageEvidence extends VoiceSessionIdentity {
  version: 1;
  observedFrom: string;
  observedTo: string;
  source: 'provider';
  /** A finalized customer subtotal, not a complete supplier invoice. Omitted for native finals. */
  billingBasis?: 'reported_modalities';
  complete: boolean;
  usage: RealtimeVoiceUsage;
}

export function assertRealtimeVoiceUsage(usage: RealtimeVoiceUsage): void {
  if (!usage || typeof usage !== 'object' || Array.isArray(usage)) throw new Error('invalid_voice_usage');
  const fields = usage.unit === 'session_duration' ? ['seconds'] : usage.unit === 'token_breakdown'
    ? ['audioInputTokens', 'audioOutputTokens', 'textInputTokens', 'textOutputTokens', 'cachedAudioInputTokens', 'cachedTextInputTokens'] : [];
  if (!fields.length || Object.keys(usage).some(key => key !== 'unit' && !fields.includes(key))) throw new Error('invalid_voice_usage');
  if (usage.unit === 'session_duration' && (typeof usage.seconds !== 'number' || !Number.isFinite(usage.seconds) || usage.seconds < 0 || usage.seconds > 86400)) throw new Error('invalid_voice_usage');
  for (const [key, value] of Object.entries(usage)) {
    if (key === 'unit' || key === 'seconds') continue;
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) throw new Error('invalid_voice_usage');
  }
  if (usage.unit === 'token_breakdown') {
    if (usage.cachedAudioInputTokens !== undefined && (usage.audioInputTokens === undefined || usage.cachedAudioInputTokens > usage.audioInputTokens)) throw new Error('invalid_voice_cache_usage');
    if (usage.cachedTextInputTokens !== undefined && (usage.textInputTokens === undefined || usage.cachedTextInputTokens > usage.textInputTokens)) throw new Error('invalid_voice_cache_usage');
  }
}

export function assertRealtimeVoiceEvidence(evidence: RealtimeVoiceUsageEvidence): void {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence) ||
    Object.keys(evidence).some(key => !['version', 'voiceSessionId', 'connectionEpoch', 'observedFrom', 'observedTo', 'source', 'complete', 'usage', 'billingBasis'].includes(key)) ||
    (evidence.billingBasis !== undefined && (evidence.billingBasis !== 'reported_modalities' || evidence.usage?.unit !== 'token_breakdown')) ||
    evidence.version !== 1 || evidence.source !== 'provider' || typeof evidence.complete !== 'boolean' ||
    typeof evidence.voiceSessionId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(evidence.voiceSessionId) ||
    !Number.isSafeInteger(evidence.connectionEpoch) || evidence.connectionEpoch < 1 ||
    typeof evidence.observedFrom !== 'string' || typeof evidence.observedTo !== 'string' ||
    !Number.isFinite(Date.parse(evidence.observedFrom)) || !Number.isFinite(Date.parse(evidence.observedTo)) || Date.parse(evidence.observedFrom) > Date.parse(evidence.observedTo)) throw new Error('invalid_voice_evidence');
  assertRealtimeVoiceUsage(evidence.usage);
  if (evidence.complete && evidence.usage.unit === 'token_breakdown' &&
    [evidence.usage.audioInputTokens, evidence.usage.audioOutputTokens, evidence.usage.textInputTokens, evidence.usage.textOutputTokens].some(value => value === undefined)) throw new Error('voice_usage_incomplete');
}

export type SpeechUsage =
  | { unit: 'audio_duration'; seconds: number }
  | Extract<RealtimeVoiceUsage, { unit: 'token_breakdown' }>;

/** Unlike realtime, a speech token rate prices only the modalities its protocol bills. */
export type SpeechRate = RealtimeVoiceRateBase & (
  | { unit: 'audio_duration'; usdPerMinute: number }
  | { unit: 'token_breakdown'; audioInputUsdPerMillion?: number; audioOutputUsdPerMillion?: number;
      textInputUsdPerMillion?: number; textOutputUsdPerMillion?: number;
      cachedAudioInputUsdPerMillion?: number; cachedTextInputUsdPerMillion?: number }
);
export type SpeechRateInput = SpeechRate extends infer R
  ? R extends SpeechRate ? Omit<R, 'version' | 'effectiveFrom'> : never : never;

/** Content-free terminal observation of one speech request; no audio, transcript or input text. */
export interface SpeechUsageEvidence {
  version: 1;
  kind: 'speech_to_text' | 'text_to_speech';
  protocol: SpeechProtocol;
  speechRequestId: string;
  observedAt: string;
  source: 'provider';
  usage: SpeechUsage;
}

export function assertSpeechUsageEvidence(evidence: SpeechUsageEvidence): void {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence) ||
    Object.keys(evidence).some(key => !['version', 'kind', 'protocol', 'speechRequestId', 'observedAt', 'source', 'usage'].includes(key)) ||
    evidence.version !== 1 || evidence.source !== 'provider' ||
    typeof evidence.speechRequestId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(evidence.speechRequestId) ||
    typeof evidence.observedAt !== 'string' || !Number.isFinite(Date.parse(evidence.observedAt))) throw new Error('invalid_speech_evidence');
  const binding = Object.prototype.hasOwnProperty.call(SPEECH_PROTOCOLS, evidence.protocol) ? SPEECH_PROTOCOLS[evidence.protocol] : undefined;
  const usage = evidence.usage;
  if (!binding || binding.kind !== evidence.kind || !usage || usage.unit !== binding.unit) throw new Error('invalid_speech_evidence');
  if (usage.unit === 'audio_duration') {
    if (Object.keys(usage).some(key => key !== 'unit' && key !== 'seconds') || typeof usage.seconds !== 'number' ||
      !Number.isFinite(usage.seconds) || usage.seconds < 0 || usage.seconds > 86400) throw new Error('invalid_speech_usage');
  } else {
    try { assertRealtimeVoiceUsage(usage); } catch { throw new Error('invalid_speech_usage'); }
    if (binding.requiredTokens.some(field => usage[field] === undefined)) throw new Error('speech_usage_incomplete');
  }
}

export type VoiceCloseReason =
  | 'user_requested' | 'idle' | 'hard_cap' | 'auth_expired' | 'access_revoked'
  | 'funding_unavailable' | 'provider_error' | 'overload' | 'drain' | 'disconnected';
export type VoiceErrorCode =
  | 'unavailable' | 'invalid_frame' | 'unsupported_version' | 'auth_required'
  | 'model_not_allowed' | 'funding_unavailable' | 'rate_limited'
  | 'provider_unavailable' | 'overload';

export type VoiceWorkStatus = 'queued' | 'running' | 'waiting_user' | 'completed' | 'failed' | 'cancelled' | 'interrupted';
export const VOICE_WORK_STATUS_COPY: Record<VoiceWorkStatus, string> = {
  queued: 'Verified application status: the confirmed task is queued. Do not claim it is completed.',
  running: 'Verified application status: the confirmed task is running. Do not claim it is completed.',
  waiting_user: 'Verified application status: the task is waiting for the user. Ask them to use the approval or question panel on screen. Spoken approval is not accepted.',
  completed: 'Verified application status: the task completed. Ask the user to review the answer, sources and files in the chat. No result contents were shared with you.',
  failed: 'Verified application status: the task failed. Ask the user to review the chat. Do not invent a successful result.',
  cancelled: 'Verified application status: the selected task was cancelled. Do not claim that external changes were undone.',
  interrupted: 'Verified application status: the task was interrupted. Ask the user to review the recovery panel and observable state before explicitly continuing. Do not replay tools.',
};

/** No provider control messages or client-authored billing evidence. */
export type VoiceClientEvent =
  | { version: 1; type: 'voice.start'; catalogModelId: string; conversationId: string; voice?: string }
  | { version: 1; type: 'voice.stop' }
  | { version: 1; type: 'work.status'; requestId: string; runId: string; status: VoiceWorkStatus; delegationId?: string }
  | { version: 1; type: 'voice.mute'; muted: boolean };

export type VoiceServerEvent =
  | ({ version: 1; type: 'voice.error'; code: VoiceErrorCode; retryable: boolean } & Partial<VoiceSessionIdentity>)
  | (VoiceSessionIdentity & { version: 1 } & (
  | { type: 'voice.started'; input: VoicePcmFormat; output: VoicePcmFormat }
  | { type: 'transcript.delta'; providerEventId?: string; speaker: 'user' | 'assistant'; text: string; startMs?: number; endMs?: number }
  // A candidate requiring explicit confirmation, never execution authorization.
  | { type: 'delegation.requested'; providerEventId?: string; delegationId: string; offsetMs?: number; candidateText?: string }
  | { type: 'delegation.cancelled'; delegationId: string }
  | { type: 'speech.interrupted' }
  | { type: 'work.context'; requestId: string; runId: string; accepted: boolean; confirmation?: 'provider' | 'transport' }
  | { type: 'usage.updated'; usage: RealtimeVoiceUsage; complete: boolean }
  | { type: 'voice.expiring'; expiresAt: string }
  | { type: 'voice.closing'; reason: VoiceCloseReason }
  | { type: 'voice.closed'; reason: VoiceCloseReason; settlement: 'settled' | 'pending_reconciliation' | 'not_dispatched' }
));

/** Also rejects unknown future kinds and malformed live routes in legacy rows. */
export function isChatCatalogItem(item: VelugaModelCatalogItem): boolean {
  return (item.interactionKind === undefined || item.interactionKind === 'chat') &&
    item.realtimeVoice === undefined && item.speech === undefined &&
    !item.routing.candidates.some(candidate =>
      VOICE_ROUTE_PROTOCOLS.includes(candidate.routeBackend?.protocol ?? ''));
}

function isPcm(value: unknown): value is VoicePcmFormat {
  if (!value || typeof value !== 'object') return false;
  const pcm = value as Record<string, unknown>;
  return Object.keys(pcm).every(key => ['encoding', 'sampleRate', 'channels'].includes(key)) &&
    pcm.encoding === 'pcm_s16le' && pcm.channels === 1 &&
    (pcm.sampleRate === 16000 || pcm.sampleRate === 24000);
}

/** Shape shared by every voice model: fixed single route on its own protocol, never a chat default. */
function isFixedVoiceModel(item: VelugaModelCatalogItem, protocol: string): boolean {
  const route = item.routing.candidates[0]?.routeBackend;
  return ['active', 'disabled'].includes(item.status) && item.defaultForProvider !== true &&
    item.thinkingLevels === undefined && item.routeKind !== 'virtual-auto' &&
    item.routing.mode === 'fixed' && item.routing.auto === undefined &&
    item.routing.candidates.length === 1 && route?.protocol === protocol &&
    (item.billingMode ?? 'platform-credit') === 'platform-credit';
}

const isVoiceList = (voices: unknown) => Array.isArray(voices) && voices.length > 0 && voices.length <= 64 &&
  new Set(voices).size === voices.length &&
  voices.every(value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value));

/** Called by both persistence adapters before any mutation. */
export function assertCatalogInteraction(item: VelugaModelCatalogItem): void {
  if (isChatCatalogItem(item)) return;
  if (item.interactionKind === 'speech_to_text' || item.interactionKind === 'text_to_speech') return assertSpeechCatalog(item);
  const voice = item.realtimeVoice;
  if (item.interactionKind !== 'realtime_voice' || !voice || item.speech !== undefined ||
    Object.keys(voice).some(key => !['protocol', 'input', 'output', 'voices', 'delegation', 'maxSessionDurationMs'].includes(key)) ||
    !isFixedVoiceModel(item, voice.protocol) ||
    !isPcm(voice.input) || !isPcm(voice.output) ||
    !Number.isSafeInteger(voice.maxSessionDurationMs) || voice.maxSessionDurationMs <= 0 ||
    voice.maxSessionDurationMs > VOICE_SESSION_HARD_CAP_MS || !isVoiceList(voice.voices)) {
    throw new Error('invalid_voice_catalog');
  }
  if (voice.protocol === 'openai-live') {
    if (item.provider !== 'openai' || voice.delegation !== 'client' ||
      voice.input.sampleRate !== voice.output.sampleRate) throw new Error('invalid_voice_catalog');
  } else if (voice.protocol === 'gemini-live') {
    if (item.provider !== 'google' || voice.delegation !== 'function_call' ||
      voice.input.sampleRate !== 16000 || voice.output.sampleRate !== 24000) throw new Error('invalid_voice_catalog');
  } else throw new Error('invalid_voice_catalog');
}

function assertSpeechCatalog(item: VelugaModelCatalogItem): void {
  const speech = item.speech;
  const binding = speech ? SPEECH_PROTOCOLS[speech.protocol] : undefined;
  const keys = binding?.kind === 'speech_to_text' ? ['protocol', 'input', 'maxInputDurationMs'] : ['protocol', 'output', 'voices', 'maxInputCodePoints'];
  if (!speech || !binding || binding.kind !== item.interactionKind || binding.provider !== item.provider ||
    item.realtimeVoice !== undefined || Object.keys(speech).some(key => !keys.includes(key)) ||
    !isFixedVoiceModel(item, speech.protocol)) throw new Error('invalid_voice_catalog');
  if ('input' in speech) {
    // D05: one-shot STT records PCM16 mono 24 kHz.
    if (!isPcm(speech.input) || speech.input.sampleRate !== 24000 ||
      !Number.isSafeInteger(speech.maxInputDurationMs) || speech.maxInputDurationMs <= 0 ||
      speech.maxInputDurationMs > SPEECH_STT_MAX_INPUT_DURATION_MS) throw new Error('invalid_voice_catalog');
  } else if (!isPcm(speech.output) || !Number.isSafeInteger(speech.maxInputCodePoints) || speech.maxInputCodePoints <= 0 ||
    speech.maxInputCodePoints > SPEECH_TTS_MAX_INPUT_CODE_POINTS || !isVoiceList(speech.voices)) throw new Error('invalid_voice_catalog');
}

export function assertModelRouteInteraction(route: ModelRoute): void {
  if (VOICE_ROUTE_PROTOCOLS.includes(route.providerProtocol) &&
    !['active', 'disabled'].includes(route.status)) throw new Error('voice_route_not_ready');
}
